"use client"

/*
 * DEV HANDOFF - destination-override.tsx
 *
 * ONE AUTOMATION'S SETTINGS FOR ONE DESTINATION - the redesign of legacy's
 * `OutputConfigModal.jsx`, titled "Settings for {account}".
 *
 * This is the THIRD of three settings screens, and it is its own page for a
 * reason: it edits a different record, on a different surface, reached from a
 * different place. The three:
 *
 *   feed-settings.tsx          an automation's own settings (`input.*`).
 *                              "Automation settings". A page.
 *   social-settings.tsx        a destination's settings (`social.params`),
 *                              shared by every automation posting to it.
 *                              "Global settings". A page.
 *   destination-override.tsx   THIS. One automation's override of those
 *                              (`output.paParams`, which is `null` for as long
 *                              as the automation inherits). A MODAL in prod.
 *
 * WHAT THE SURFACE COSTS IT: no back link (a back link inside a dialog points
 * nowhere) and no delete card. Legacy does carry a "Deactivate posting" action
 * here (`SocialPanelModule.js:237`, whenever `output.id` is set) - dropped,
 * because the "Post to this destination" switch in the list now does that job
 * and legacy having both is the redundancy, not a feature we are missing. Open
 * in HANDOFF if that reading is wrong.
 *
 * RENDERS THE DIALOG SURFACE, NOT A PORTALING `<Dialog>` - the same convention
 * `welcome-modal.tsx` and `new-social-suggest-feeds.tsx` follow, so it stays
 * self-contained and pixel-reviewable in the gallery. In prod, drop this
 * inside your `Dialog` and remember the `.tw-root` portal patch on
 * `DialogContent`.
 *
 * Its two siblings are pages and this is a dialog, which is the whole reason
 * the shell was split: `SettingsBody` is the part all three share (tab strip,
 * dirty dots, fields, skeletons) and each surface supplies its own chrome
 * around it. A page gets `SettingsPage`'s heading and footer; this gets a
 * dialog header, a scrolling body and a pinned footer.
 *
 *   settings-page.tsx  `SettingsBody` + `FormFooter`, shared
 *   settings-form.tsx  the divided settings list
 * plus `destination-override.data.ts`, which is MOCK DATA and does not ship.
 *
 * WIRING (production owns all of it):
 * - `OutputConfigModal.jsx` keeps state, the dirty diff, save / cancel and the
 *   `ErrorModule` plumbing; only the panel body is replaced.
 * - `sections` is the whole data contract. Build it from the output record
 *   falling back to the social record; every field carries its legacy
 *   `data-field` dot path in `SettingsField.name`.
 * - The inherit switch writes `output.paParams`: `null` to inherit, a copy of
 *   the destination's params to start overriding. Legacy's toggle is
 *   `toggleDefaultUsage` in `OutputConfigModal.jsx:291`.
 */

import * as React from "react"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { HELP_TEXT } from "@/lib/ui-tokens"
import { SM_BUTTON } from "@/shadcn/components/styles"
import { Button } from "@/shadcn/components/ui/button"
import {
  DESTINATION_SLUG,
  NETWORK_LABEL,
  type DestinationNetwork,
} from "@/shadcn/components/settings/settings-vocab"
/*
 * H5 IS ENTIRELY A DATA RULE, so there is no `sourceType` prop here.
 * `buildOverrideSections({ sourceType })` swaps the four content switches for
 * one message, and this component renders whatever `sections` it is handed.
 * A prop the component accepted but never read would be the same fault as the
 * inherit switch's inert direction (C2).
 */
import {
  SettingsFormProvider,
  type SettingsSection,
} from "@/shadcn/components/settings/settings-form"
import {
  FormFooter,
  SettingsBody,
  SettingsDeleteDialog,
  type SettingsPageProps,
  SettingsAvatar,
} from "@/shadcn/components/settings/settings-page"

/*
 * THE OMITS ARE THE POINT: a dialog has none of these, so the type says so
 * rather than accepting them and quietly doing nothing.
 *
 * - `backLabel` - a back link inside a dialog points nowhere.
 * - `note` / `onDismissNote` - `OutputConfigModal` has no banner, and scope is
 *   already said by the title, the meta line and the inherit switch.
 * - `actions` - the header carries a close button, not page actions. Legacy's
 *   modal has no Error log either.
 * - the delete four - you do not delete a destination from inside one
 *   automation.
 */
type DestinationOverrideProps = Omit<
  Partial<SettingsPageProps>,
  | "backLabel"
  | "note"
  | "onDismissNote"
  | "actions"
  | "showDelete"
  | "deleteTitle"
  | "deleteDescription"
  | "deleteLabel"
  | "onDelete"
> & {
  /**
   * REQUIRED HERE, unlike the preview kit, where it defaults to a mock
   * fixture. Prod builds this from the stored record; there is no fixture to
   * fall back to. See `settings-vocab.ts` for why the split exists.
   */
  sections: SettingsSection[]
  /** Closes the dialog. Prod wires this to its own `Dialog` state. */
  onClose?: () => void
  /** Drives the default meta line. The tab set comes from `sections`. */
  network?: DestinationNetwork
  /** The connected account whose settings these are, e.g. "samsamspdx". */
  accountName?: string
  /**
   * The account's picture, as on its Global settings page: `social.icon`,
   * else `social.src`. Omitted, or failing to load, it falls back to initials.
   */
  avatarUrl?: string
  /**
   * WHICH UI THIS SCREEN IS BEING SHOWN IN, because the two name this level
   * differently and the rule is that each keeps its own word (Emily's call;
   * LEGACY-QUESTIONS I5). Basic calls the thing that owns destinations an
   * **automation**; complex calls it a **route**. This screen is reached from
   * both UIs - destinations hang off the route's output group either way
   * (`Route.php`) - so it cannot hardcode either word.
   *
   * Prod passes whichever matches `app.flags.routeViewType`:
   * `ROUTE_VIEW_SIMPLE` -> "automation", `ROUTE_VIEW_ADVANCED` -> "route".
   */
  scope?: "automation" | "route"
  /** The automation's or route's name, e.g. "TechCrunch". */
  scopeName?: string
  /**
   * Whether this automation is still using the destination's settings.
   *
   * DERIVED, NOT CHOSEN. In legacy it is `output.paParams === null`
   * (`OutputConfigModal.jsx:216`), so editing ANY field populates `paParams`
   * and this becomes false on its own. Production passes the derived value;
   * nothing here sets it.
   */
  inherit?: boolean
  /**
   * Discard the override and go back to the destination's settings. The ONLY
   * manual move this state has - see the band below. Prod sets
   * `output.paParams = null`.
   */
  onReset?: () => void
  /**
   * B7: remove THIS automation's output, leaving the account connected.
   *
   * Mark settled that there are two deletes with different blast radii: *"If
   * the user is deleting a social from `/config/social/{$socialId}`, the
   * social will be deleted from all automations. If the user is deleting an
   * output from an automation, the social isn't removed, only the instance
   * (aka output) tied to that particular automation."*
   *
   * HANDOFF used to justify leaving this off with "you do not delete a
   * destination from inside one automation". That was wrong: legacy has it
   * (`OutputConfigModal.jsx:215` sets `deleteAction = this.deleteOutput`) and
   * Mark's answer describes it.
   */
  onRemove?: () => void
  /**
   * Whether the remove action shows. SEPARATE FROM `onRemove` on purpose:
   * visibility keyed to a callback's existence cannot be previewed, because
   * the preview spec is a Server Component and a function cannot cross that
   * boundary - the state 500s. It also reads badly as an API. Matches
   * `SettingsPage`'s `showDelete`, which exists for the same reason.
   */
  showRemove?: boolean
  /**
   * Overrides the remove button's text.
   *
   * EXISTS FOR THE CONFIRM STEP. Legacy asks twice by relabelling the button
   * — "Are you sure? One more click to deactivate" — rather than stacking a
   * dialog on a dialog. Without a way to say that, the first click changes
   * nothing on screen and the second removes the output, which is a silent
   * destructive action.
   */
  removeLabel?: string
}

export default function DestinationOverride({
  sections,
  network = "twitter",
  accountName = "samsamspdx",
  avatarUrl,
  scope = "automation",
  scopeName = "TechCrunch",
  inherit = false,
  onReset,
  onRemove,
  showRemove = false,
  removeLabel,
  title,
  meta,
  className,
  dirty,
  saving,
  loading,
  /* Pulled out of `props` rather than spread: it belongs to the provider, and
     `SettingsBody` — which takes the rest — would swallow it silently. */
  onChange,
  onSave,
  onCancel,
  onClose,
  ...props
}: DestinationOverrideProps) {
  /* Explicit `dirty` still wins; otherwise any dirty section makes it dirty -
     the same derivation `SettingsPage` uses, so the two surfaces agree. */
  const isDirty = dirty ?? Boolean(props.dirtySections?.length)
  /*
   * INHERITANCE IS A STATUS PLUS ONE ACTION, NOT A SWITCH.
   *
   * This was a two-state toggle until LEGACY-QUESTIONS C2, and that was
   * wrong in a way worth spelling out, because the legacy control invites the
   * same mistake. Three facts from `OutputConfigModal.jsx`:
   *
   *   :216  isActive = (this.state.output.paParams === null)
   *   :131  toggleDefaultUsage - `if (!nextState) return;`
   *   :135  ...otherwise `output.paParams = null`
   *
   * So the state is DERIVED: editing any field populates `paParams` and the
   * control turns itself off. Clicking it ON discards the whole override.
   * Clicking it OFF does nothing at all - the handler returns immediately.
   *
   * There is therefore no way to "start overriding" from the control. You
   * start overriding by editing a field. Our old help line said "turn this
   * off to give it its own settings", which is an instruction to do an inert
   * thing: the user clicks and nothing happens.
   *
   * Hence a status line, with the reset as a BUTTON and only when there is
   * something to reset. That removes the inert direction rather than
   * documenting it, and it puts the one real action in the affordance that
   * matches it. It also follows the rule C4 settled: the default goes
   * unmarked, the exception carries the marker. Inheriting is the default and
   * stays quiet; overriding states itself and offers the way back.
   *
   * It composes with the per-field `Override` badges rather than competing:
   * the badges say WHICH fields differ, this band says what state the record
   * is in and what can be done about it.
   *
   * LEGACY PUTS THE AUTOMATION'S NAME AND ICON IN THIS SAME BAND
   * (`OutputConfigModal.jsx:277`, `.output-defaults` = `.route-reference` +
   * `.default-toggle`), so the thing being overridden is named beside the
   * control that governs it. Here the name is in the meta line instead.
   * Legacy's arrangement is the better one and is an open item in HANDOFF.
   */
  const inheritRow = (
    /* Wraps at phone width with the text on its own line: the button label
       carries an account name and can be long. `basis-full` below `sm` is
       what makes it wrap at all - see the delete card and A7. */
    <div className="tw:flex tw:w-full tw:flex-wrap tw:items-center tw:gap-x-4 tw:gap-y-3 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-4">
      <div className="tw:flex tw:min-w-0 tw:shrink tw:grow tw:basis-full tw:flex-col tw:gap-1 tw:sm:basis-0">
        <p className="tw:text-sm tw:font-medium tw:text-foreground">
          {inherit
            ? `Using ${accountName}'s settings`
            : `This ${scope} has its own settings`}
        </p>
        {/* The inheriting line teaches the real mechanism, because the control
            no longer implies one. */}
        <p className={HELP_TEXT}>
          {inherit
            ? /*
               * PER-FIELD AGAIN, because C12 is fixed. This said "stops
               * following {account} for all of them", which was true while the
               * override was stored as a full snapshot: editing one field gave
               * the automation its own copy of every setting. `trimParams` in
               * `OutputConfigModal` now stores only the keys that differ
               * (prod `d71ee196f`), and `OutputRule::getParams` merges the
               * rest from the destination, so the blast radius really is one
               * field. The line was marked contingent on that; this is the
               * contingency landing.
               */
              `Change any setting below and this ${scope} keeps its own value for that one. The rest stay in step with ${accountName}.`
            : `It stopped using ${accountName}'s settings when something below was changed.`}
        </p>
      </div>
      {!inherit ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(SM_BUTTON, "tw:shrink-0")}
          onClick={onReset}
        >
          {/* Template literal, not `&rsquo;`, so the apostrophe matches the two
              lines above it - they are template literals and render a straight
              quote. Mixing the two styles in one band is visible. */}
          {`Reset to ${accountName}'s settings`}
        </Button>
      ) : null}
    </div>
  )

  return (
    <SettingsFormProvider onChange={onChange}>
      <div
        className={cn(
          /*
           * 640px, not the pages' 968. A dialog is a focused task and the
           * rows are label-left / control-right, so a wider box just pushes
           * each switch further from the thing it is labelled by - the exact
           * problem the pages' own max-width exists to prevent. The 480px
           * control cap still fits inside it.
           */
          "tw:relative tw:mx-auto tw:flex tw:max-h-[calc(100dvh-2rem)] tw:w-full tw:max-w-[640px] tw:flex-col tw:overflow-hidden tw:rounded-xl tw:border tw:border-border tw:bg-card tw:font-sans tw:text-card-foreground tw:shadow-lg",
          className
        )}
      >
        {/*
          * HEADER. The title is an `h2`, not the pages' `h1`: a dialog is not
          * the document's top-level heading, and prod will hand this to
          * `DialogTitle` anyway. Legacy draws a dark bar with the title
          * centred; this is the kit's own card header, because the redesign
          * does not carry legacy's chrome over.
          */}
        <div className="tw:flex tw:shrink-0 tw:items-start tw:gap-4 tw:border-b tw:border-border tw:p-4 tw:md:p-6">
          {/* The account's avatar, as its Global settings page draws it.
              Beside both lines rather than the title alone, centred on the
              pair, as the dialog keeps its title and meta together. */}
          <div className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-3">
            <SettingsAvatar
              name={accountName}
              avatarUrl={avatarUrl}
              network={DESTINATION_SLUG[network]}
            />
            <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-1">
              <h2 className="tw:text-lg tw:leading-tight tw:font-semibold tw:text-foreground">
                {title ?? `Settings for ${accountName}`}
              </h2>
              <p className="tw:text-sm tw:text-muted-foreground">
                {meta ?? `${scopeName} · ${NETWORK_LABEL[network]}`}
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Close"
            className="tw:-mt-1 tw:-mr-1 tw:shrink-0 tw:text-muted-foreground tw:hover:text-foreground"
            onClick={onClose}
          >
            <X />
          </Button>
        </div>

        {/*
          * BODY SCROLLS, HEADER AND FOOTER STAY.
          *
          * THE CARD IS CAPPED, NOT THE BODY. A fixed `max-h` on the scroll box
          * cannot know what the header and footer above and below it cost, so
          * the card grew past the viewport and `DialogContent`, which centres
          * with `top-1/2 -translate-y-1/2`, clipped it at both ends — and
          * nothing scrolled, because the overflow sat on a box that never
          * exceeded its own cap. At 550px tall the whole footer was gone.
          *
          * So the CARD takes the viewport cap and the body is the shrinkable
          * part: `flex-1` to take what is left, and `min-h-0` because a flex
          * item will not shrink below its content without it — the same trap
          * that squashed the inherit card, one level up.
          *
          * `dvh` rather than `vh` so a mobile browser's collapsing toolbar is
          * counted; the 2rem matches the horizontal inset `DialogContent`
          * already keeps.
          *
          * THE KIT COPY DIFFERS HERE, and is meant to: its 520px exists
          * because the preview harness sizes its iframe FROM the measured
          * content, so a viewport unit there feeds the iframe's own height
          * back into the content and never settles. Its own note says prod
          * should use its own height handling instead of that number. This is
          * that.
          */}
        <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto">
          {/*
            * TWO ELEMENTS: the scroll box, then the column inside it.
            *
            * They were one, and that broke the inherit card. A `flex-col` box
            * that is ALSO height-capped makes its children flex items with
            * the default `flex-shrink: 1`, so when the content ran past 520px
            * the first child was squashed - measured at 2px, just its two
            * borders, with its 84px of content clipped by `overflow-hidden`.
            * Capping the height on the outer box and laying out in an
            * unconstrained inner one means nothing shrinks and the overflow
            * scrolls, which is what was wanted.
            */}
          <div className="tw:flex tw:flex-col tw:gap-6 tw:p-4 tw:md:p-6">
            {/* Placed at the END OF THE SCROLLING BODY, not as a danger-zone
                card and not in the footer. Legacy passes `deleteHandler` into
                the panel body, so it scrolls with the content; a card would
                fight a 640px dialog, and the footer belongs to Save and
                Cancel. */}
            <SettingsBody
              sections={sections}
              beforeTabs={inheritRow}
              loading={loading}
              {...props}
            />
          </div>
        </div>

        {/*
          * THE FOOTER STAYS WHILE LOADING, unlike the pages, which hide it
          * until the record lands. A page without a footer still reads as a
          * page; a dialog without its action bar reads as broken, and there is
          * no way to close it but the X. Save is disabled anyway - nothing is
          * dirty - so the bar is present and inert rather than absent.
          */}
        {showRemove ? (
          <div className="tw:shrink-0 tw:border-t tw:border-border tw:px-4 tw:py-3 tw:md:px-6">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={cn(SM_BUTTON, "tw:text-destructive tw:hover:text-destructive")}
              onClick={onRemove}
            >
              {removeLabel ?? `Remove this output from ${scopeName}`}
            </Button>
          </div>
        ) : null}

        <div className="tw:shrink-0 tw:border-t tw:border-border tw:p-4 tw:md:p-6">
          <FormFooter
            dirty={isDirty}
            saving={saving}
            onSave={onSave}
            onCancel={onCancel}
          />
        </div>
      </div>
    </SettingsFormProvider>
  )
}

/**
 * The reset confirm.
 *
 * WHY A CONFIRM AT ALL, since legacy has none: resetting discards EVERY
 * override on the record at once. C3 established that reverting is not
 * per-field - the data model has no such concept - so there is nothing to
 * undo it with. C5 already replaced legacy's click-twice confirms with real
 * dialogs, so this is the same ruling applied to the other destructive move
 * on this surface. Emily approved the confirm with the build.
 *
 * Reuses `SettingsDeleteDialog` rather than forking a second confirm surface.
 * FLAGGED, not decided: that surface is destructive-toned - a red triangle in
 * a tinted circle and a destructive button. Discarding someone's settings
 * irreversibly earns that, but if it reads too hot for a reset, the fix is to
 * give the shared dialog a tone prop rather than to clone it here.
 */
export function ResetOverrideDialog({
  accountName = "samsamspdx",
  scope = "automation",
  ...props
}: Omit<
  React.ComponentProps<typeof SettingsDeleteDialog>,
  "title" | "description" | "confirmLabel"
> & {
  accountName?: string
  scope?: "automation" | "route"
}) {
  return (
    <SettingsDeleteDialog
      title="Reset to the output's settings?"
      description={`This ${scope} will go back to using ${accountName}'s settings, and the changes made here will be discarded. Other automations posting to ${accountName} are unaffected. This cannot be undone.`}
      confirmLabel="Reset settings"
      {...props}
    />
  )
}

/**
 * B7's confirm, and the copy is doing the load-bearing work.
 *
 * It has to say what this DOES NOT do, because the other delete on these
 * screens - "Delete output" on the global screen - removes the account from
 * every automation. Mark: only the instance goes here. So this names the
 * automation, says the account stays, and says the other automations are
 * unaffected.
 *
 * Reuses `SettingsDeleteDialog` rather than a third confirm surface, as
 * `DeleteDestinationDialog` and `ResetOverrideDialog` both do.
 */
export function RemoveOutputDialog({
  accountName = "samsamspdx",
  scope = "automation",
  scopeName = "TechCrunch",
  ...props
}: Omit<
  React.ComponentProps<typeof SettingsDeleteDialog>,
  "title" | "description" | "confirmLabel"
> & {
  accountName?: string
  scope?: "automation" | "route"
  scopeName?: string
}) {
  return (
    <SettingsDeleteDialog
      title={`Remove this output from ${scopeName}?`}
      description={`${scopeName} will stop posting to ${accountName}. The account stays connected and every other ${scope} posting to it is unaffected. Posts we already published stay up.`}
      confirmLabel="Remove output"
      {...props}
    />
  )
}
